import type { TextLayer } from "@memeup/design/browser";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { DesignSvgContent, useMemeFont } from "../components/DesignPreview.tsx";
import { Icon } from "../components/Icon.tsx";
import { Mockup } from "../components/Mockup.tsx";
import { MakeSlot, ProductSticker } from "../components/ProductSticker.tsx";
import { useConfig } from "../components/Shell.tsx";
import { Sticker } from "../components/Sticker.tsx";
import { api, call } from "../lib/api.ts";
import { money } from "../lib/format.ts";

export function useProducts() {
  return useQuery({
    queryKey: ["products"],
    queryFn: () => call(api.products.$get({ query: {} })),
  });
}
export function useStudioData() {
  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: () => call(api.studio.templates.$get()),
  });
  const bases = useQuery({
    queryKey: ["base-products"],
    queryFn: () => call(api.studio["base-products"].$get()),
  });
  return { templates, bases };
}

function Hero() {
  const products = useProducts();
  const cfg = useConfig();
  const currency = cfg.data?.currency ?? "EUR";
  const side = products.data?.products.slice(0, 2) ?? [];
  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="hero__lead">
        <Sticker
          face="var(--tomato)"
          faceText="var(--white)"
          tilt={-4}
          className="hero__sticker"
          over={
            <Link to="/make" className="hero__flap">
              <span>write your own</span>
              <Icon name="arrow" size={18} />
            </Link>
          }
        >
          <div className="hero__copy">
            <h1 id="hero-title">
              <span>Peel it.</span>
              <br />
              <span>Wear it.</span>
            </h1>
            <p>
              Custom meme merch. Write your own caption, we print it on a tee and ship it to your
              door.
            </p>
          </div>
        </Sticker>
        <div className="hero__actions">
          <Link to="/make" className="btn">
            <Icon name="pen" /> Make your own
          </Link>
          <a href="#sheet" className="btn btn--white">
            Shop ready-made
          </a>
        </div>
      </div>
      <div className="hero__side">
        {side.length === 0 && products.isLoading
          ? [0, 1].map((i) => <div key={i} className="skeleton hero__skeleton" />)
          : side.map((p, i) => (
              <ProductSticker
                key={p.id}
                product={p}
                index={i}
                currency={currency}
                shape={i === 0 ? "round" : "rect"}
                tilt={i === 0 ? 0 : 5}
              />
            ))}
      </div>
    </section>
  );
}

/** Templates that suit the demo caption, best first (matched by slug prefix). */
const DEMO_TEMPLATES = ["philosoraptor", "success-kid", "bad-luck-brian"];
/** Blank canvases for the customer's own image: nothing to show on their own. */
const BLANK_TEMPLATES = ["transparent", "top-bottom"];

function pickDemoTemplate<T extends { slug: string; slots: Array<{ valign: string }> }>(
  list: T[],
): T | undefined {
  for (const name of DEMO_TEMPLATES) {
    const hit = list.find((t) => t.slug.startsWith(`${name}-`));
    if (hit) return hit;
  }
  return (
    list.find(
      (t) =>
        !BLANK_TEMPLATES.some((b) => t.slug.startsWith(`${b}-`)) &&
        t.slots[0]?.valign === "top" &&
        t.slots[1]?.valign === "bottom",
    ) ?? list[0]
  );
}

/** Proof of the mechanism, right on the homepage: type and watch your shirt change. */
function TryIt() {
  const { templates, bases } = useStudioData();
  const font = useMemeFont();
  const navigate = useNavigate();
  const cfg = useConfig();
  const [top, setTop] = useState("me explaining memes");
  const [bottom, setBottom] = useState("to my parents");

  const tpl = pickDemoTemplate(templates.data?.templates ?? []);
  const base = bases.data?.baseProducts[0];
  const black = base?.variants.find((v) => /black/i.test(v.title)) ?? base?.variants[0];
  const layers = useMemo<TextLayer[]>(() => {
    if (!tpl) return [];
    const [a, b] = tpl.slots;
    return [
      ...(a ? [{ id: a.id, box: a.box, valign: a.valign, text: top }] : []),
      ...(b ? [{ id: b.id, box: b.box, valign: b.valign, text: bottom }] : []),
    ];
  }, [tpl, top, bottom]);

  if (!tpl || !base) return null;
  const go = () => {
    const q = new URLSearchParams({ template: tpl.id, base: base.id, t0: top, t1: bottom });
    navigate(`/make?${q}`);
  };
  return (
    <section className="section tryit" aria-labelledby="tryit-title">
      <div className="tryit__copy">
        <h2 id="tryit-title">Type it. It's on the shirt.</h2>
        <p className="lede">
          This is the real preview: the same renderer makes your print file, so what you see is what
          lands on the cotton.
        </p>
        <form
          className="tryit__form"
          onSubmit={(e) => {
            e.preventDefault();
            go();
          }}
        >
          <div className="field">
            <label htmlFor="try-top">Top text</label>
            <input
              id="try-top"
              className="input"
              value={top}
              maxLength={80}
              onChange={(e) => setTop(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="try-bottom">Bottom text</label>
            <input
              id="try-bottom"
              className="input"
              value={bottom}
              maxLength={80}
              onChange={(e) => setBottom(e.target.value)}
            />
          </div>
          <button type="submit" className="btn">
            Finish it in the Studio <Icon name="arrow" />
          </button>
          <p className="tryit__price">
            {base.name} from <strong>{money(base.fromPriceCents, cfg.data?.currency)}</strong>
          </p>
        </form>
      </div>
      <Sticker face="var(--sky)" tilt={2} className="tryit__stage">
        <Mockup
          spec={base.mockup}
          color={black?.swatch}
          fit={base.printFit}
          aspect={{ width: tpl.width, height: tpl.height }}
          label={`${base.name} preview with your caption`}
          className="tryit__mockup"
        >
          <DesignSvgContent
            artwork={{ url: tpl.previewUrl, width: tpl.width, height: tpl.height }}
            layers={layers}
            style={{ fontId: "anton", fill: "#FFFFFF", stroke: "#000000", uppercase: true }}
            font={font}
          />
        </Mockup>
      </Sticker>
    </section>
  );
}

function TheSheet() {
  const products = useProducts();
  const cfg = useConfig();
  const list = products.data?.products ?? [];
  return (
    <section id="sheet" className="section" aria-labelledby="sheet-title">
      <div className="section-head">
        <h2 id="sheet-title">Ready to peel</h2>
        <p>Designed by us, printed when you order. Tap + to grab one in your size.</p>
      </div>
      {products.isError ? (
        <div className="notice" data-tone="error">
          We couldn't load the shop.{" "}
          <button type="button" className="link-btn" onClick={() => products.refetch()}>
            Try again
          </button>
        </div>
      ) : (
        <div className="sheetgrid sheetgrid--lead">
          {products.isLoading
            ? [0, 1, 2].map((i) => <div key={i} className="skeleton sheetgrid__skeleton" />)
            : list.map((p, i) => (
                <ProductSticker
                  key={p.id}
                  product={p}
                  index={i + 1}
                  shape={i % 3 === 1 ? "round" : "rect"}
                  currency={cfg.data?.currency ?? "EUR"}
                />
              ))}
          <MakeSlot />
        </div>
      )}
    </section>
  );
}

function ShareTeaser() {
  return (
    <section className="section share-teaser" aria-labelledby="share-title">
      <Sticker face="var(--yolk)" tilt={-1.5} className="share-teaser__sticker">
        <div className="share-teaser__body">
          <h2 id="share-title">Made one? Send the link.</h2>
          <p>
            Every design gets its own page. Drop it in the group chat and your friends can order the
            same shirt in their size.
          </p>
          <div className="share-teaser__link" aria-hidden="true">
            <Icon name="link" /> {window.location.host}/d/your-meme
          </div>
          <Link to="/make" className="btn">
            Start a design <Icon name="arrow" />
          </Link>
        </div>
      </Sticker>
    </section>
  );
}

export function HomePage() {
  return (
    <div className="sheet">
      <Hero />
      <TheSheet />
      <TryIt />
      <ShareTeaser />
    </div>
  );
}
